import { useDroppable, useDndMonitor } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { SearchContext } from "context/providers";
import { SKILL_TYPES } from "lookup";
import { Fragment, memo, useContext, useRef, useState } from "react";
import { cn } from "utils";
import { parseSkillsString } from "utils/helpers/search";

import Chip from "./Chip";
import DropIndicator from "./DropIndicator";
import HiddenDroppable, { HIDDEN_DROPPABLE_ID } from "./HiddenDroppable";
import InputInline from "./InputInline";

const TITLES = {
  [SKILL_TYPES.REQUIRED]: "MUST-HAVE",
  [SKILL_TYPES.OPTIONAL]: "NICE-TO-HAVE",
};

function SortableWrapper({ skillType, skills, altSkills }) {
  const { setNodeRef, isOver: isOverContainer } = useDroppable({
    id: skillType,
  });
  const { setSkillSearchState, searchState } = useContext(SearchContext);

  const [overId, setOverId] = useState(null);

  useDndMonitor({
    onDragOver: (event) => {
      setOverId(event.over?.id ?? null);
    },
    onDragEnd: () => setOverId(null),
    onDragCancel: () => setOverId(null),
  });

  const inputRef = useRef(null);

  const handleWrapperClick = (e) => {
    if (e.target.closest(".ai-skill-chip")) return;
    inputRef.current?.focus();
  };

  const handleOnAdd = (input) => {
    const skillNames = parseSkillsString(input);

    const otherType =
      skillType === SKILL_TYPES.REQUIRED
        ? SKILL_TYPES.OPTIONAL
        : SKILL_TYPES.REQUIRED;

    const currentSkills = [...searchState.skills[skillType]];
    const updatedAltSkills = [...altSkills];

    skillNames.forEach((skillName) => {
      if (currentSkills.some((s) => s.name === skillName)) return;

      const skillFromAISkills = searchState.skills.aiSkills.find(
        (s) => s.name === skillName
      );

      const skillFromAlt = updatedAltSkills.find((s) => s.name === skillName);

      const newSkill = skillFromAISkills ||
        skillFromAlt || {
          name: skillName,
          active: true,
          ai: { isGenerated: false },
        };

      currentSkills.push(newSkill);

      const altIndex = updatedAltSkills.findIndex((s) => s.name === skillName);
      if (altIndex !== -1) {
        updatedAltSkills.splice(altIndex, 1);
      }
    });

    setSkillSearchState({
      ...searchState.skills,
      [skillType]: currentSkills,
      [otherType]: updatedAltSkills,
    });
  };

  const handleClear = () => {
    setSkillSearchState({
      ...searchState.skills,
      [skillType]: [],
    });
  };

  const trailingDropzoneName = HIDDEN_DROPPABLE_ID(skillType);
  const skillsWithTailingDropzone = [...skills, { name: trailingDropzoneName }];

  return (
    <div
      ref={setNodeRef}
      className={cn("flex gap-2 flex-col", {
        "flex-[2]": skillType === SKILL_TYPES.OPTIONAL,
        "flex-1": skillType === SKILL_TYPES.REQUIRED,
      })}
    >
      <div className="flex w-full justify-between items-center">
        <h2 className="font-semibold text-gray-500">{TITLES[skillType]}</h2>
        <button
          className="text-sky-600 h-min text-sm font-medium hover:underline"
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
      <SortableContext
        items={skillsWithTailingDropzone.map((skill) => skill.name)}
        strategy={rectSortingStrategy}
      >
        <div
          onClick={handleWrapperClick}
          className="w-full flex-1 flex flex-wrap gap-3 p-4 border border-gray-300 rounded-md min-w-[380px] min-h-[80px] items-start"
        >
          <div className="w-full flex flex-wrap gap-3">
            {skillsWithTailingDropzone.length > 0 ? (
              skillsWithTailingDropzone.map((skill) => {
                if (skill.name === trailingDropzoneName) {
                  return (
                    <HiddenDroppable
                      key="hidden"
                      shouldDisplayIndicator={isOverContainer}
                      skillType={skillType}
                    />
                  );
                }
                return (
                  <Fragment key={skill.name}>
                    {overId === skill.name && <DropIndicator />}
                    <Chip skill={skill} skillType={skillType} />
                  </Fragment>
                );
              })
            ) : (
              <div className="min-h-[32px] w-full h-full" />
            )}
          </div>
          <InputInline
            ref={inputRef}
            skillType={skillType}
            onAdd={handleOnAdd}
          />
        </div>
      </SortableContext>
    </div>
  );
}

export default memo(SortableWrapper, (prev, next) => {
  return (
    prev.skills === next.skills &&
    prev.altSkills === next.altSkills &&
    prev.skillType === next.skillType
  );
});
